import { CheckCircle, WarningCircle, XCircle } from '@phosphor-icons/react';
import { formatPercent } from '../../utils/formatters';
import { useDealCurrency } from '../../context/DealCurrencyContext';

function GradeBadge({ grade }) {
  const styles = {
    PASS: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    WEAK: 'bg-amber-100 text-amber-700 border-amber-200',
    FAIL: 'bg-red-100 text-red-700 border-red-200',
  };
  const icons = {
    PASS: <CheckCircle weight="fill" className="w-3 h-3" />,
    WEAK: <WarningCircle weight="fill" className="w-3 h-3" />,
    FAIL: <XCircle weight="fill" className="w-3 h-3" />,
  };
  return (
    <span className={`inline-flex items-center gap-0.5 text-[8px] font-bold px-2 py-0.5 rounded border ${styles[grade]}`} data-testid={`grade-${grade.toLowerCase()}`}>
      {icons[grade]} {grade}
    </span>
  );
}

export function DealQualityTab({
  outputs,
  outputsChf = {},
  decision,
  qualityGrade,
  combinedVerdict = null,
}) {
  const money = useDealCurrency();
  const roiGrade = qualityGrade.roiGrade || 'WEAK';
  const marginGrade = qualityGrade.marginGrade || 'WEAK';
  const profitGrade = qualityGrade.profitGrade || 'WEAK';
  const landRatioGrade = qualityGrade.landRatioGrade || 'WEAK';
  const overall = qualityGrade.overall || 'WEAK';
  // Final verdict includes land when App/share pass combinedVerdict; financial-only is fallback.
  const effectiveVerdict = combinedVerdict?.verdict
    || qualityGrade.effectiveVerdict
    || decision.verdict;
  const effectiveReason = combinedVerdict?.reason
    || qualityGrade.effectiveReason
    || decision.reason;
  const inputsInvalid = qualityGrade.inputsInvalid || (qualityGrade.invalidInputs || []).length > 0;
  const invalidInputs = qualityGrade.invalidInputs || [];
  const thresholds = qualityGrade.thresholds || {};
  const roiTargetPct = thresholds.roi?.pass != null ? Math.round(thresholds.roi.pass * 100) : null;
  const marginTargetPct = thresholds.margin?.pass != null ? Math.round(thresholds.margin.pass * 100) : null;
  const landMaxPct = thresholds.landRatio?.pass_below != null ? Math.round(thresholds.landRatio.pass_below * 100) : null;

  const positionLabel = {
    premium: 'Premium (+15%)', high: 'High (+5-15%)', market: 'Market',
    competitive: 'Competitive (-5-15%)', aggressive: 'Aggressive (-15%)',
  };

  return (
    <div className="space-y-3 p-3" data-testid="deal-quality-tab">
      {/* Overall Grade */}
      <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3 border border-slate-200">
        <div>
          <div className="text-[8px] text-slate-400 uppercase tracking-wide font-bold">Deal Quality</div>
          <div className="text-[10px] text-slate-600 mt-0.5">Is this deal worth doing?</div>
        </div>
        <GradeBadge grade={overall} />
      </div>

      {/* Invalid / extreme inputs banner */}
      {inputsInvalid && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-2 text-[9px] text-red-800" data-testid="invalid-inputs-banner">
          <div className="font-bold mb-1">Invalid or extreme inputs — scoring blocked</div>
          <ul className="list-disc pl-3 space-y-0.5">
            {invalidInputs.map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
          </ul>
        </div>
      )}

      {/* FAIL override notice */}
      {overall === 'FAIL' && !inputsInvalid && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-[9px] text-red-700 font-medium" data-testid="fail-override-notice">
          Deal Quality is FAIL — verdict is NO GO regardless of survivability.
        </div>
      )}

      {/* Metrics — ops currency (P&L Summary alone stays CHF) */}
      <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">
        {[
          { label: 'ROI on Capital at Risk', value: formatPercent(outputs.roiOnEquity), sub: roiTargetPct != null ? `Final Net / Capital Invested · Target: ~${roiTargetPct}%` : 'Final Net / Capital Invested', grade: roiGrade },
          { label: 'Margin (PAT)', value: formatPercent(outputs.marginPercent), sub: marginTargetPct != null ? `Target: ~${marginTargetPct}%` : null, grade: marginGrade },
          { label: 'Net Profit', value: money.formatOp(money.opVal('finalNetProfit')), sub: `Operating ${money.op}`, grade: profitGrade },
          { label: 'Land / Revenue', value: formatPercent(outputs.landToRevenueRatio), sub: landMaxPct != null ? `Max: ~${landMaxPct}%` : null, grade: landRatioGrade },
        ].map(m => (
          <div key={m.label} className="flex items-center justify-between px-3 py-2">
            <div>
              <div className="text-[10px] text-slate-700 font-medium">{m.label}</div>
              {m.sub && <div className="text-[7px] text-slate-400">{m.sub}</div>}
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-[11px] font-bold tabular-nums ${inputsInvalid ? 'text-slate-400' : 'text-slate-800'}`}>{m.value}</span>
              <GradeBadge grade={m.grade} />
            </div>
          </div>
        ))}
      </div>

      {/* Market Position — suppressed when inputs are invalid */}
      <div className="bg-white rounded-lg border border-slate-200 p-3">
        <div className="text-[8px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">Market Position</div>
        {inputsInvalid ? (
          <div className="text-[9px] text-slate-500 italic" data-testid="market-position-suppressed">
            Market comparison hidden until inputs are within valid ranges.
          </div>
        ) : (
          <div className="space-y-1">
            {[
              { label: 'Positioning', value: positionLabel[outputs.pricePositioning] || outputs.pricePositioning },
              { label: 'Price vs Market', value: `${outputs.priceDelta > 0 ? '+' : ''}${(outputs.priceDelta * 100).toFixed(1)}%`, color: outputs.priceDelta > 0.05 ? 'text-amber-600' : outputs.priceDelta < -0.05 ? 'text-emerald-600' : '' },
              { label: `${money.op}/m²`, value: money.formatOp(money.opVal('pricePerSqmEffective')) },
              { label: 'Unit Price', value: money.formatOp(money.opVal('pricePerUnitWithParking')) },
            ].map(r => (
              <div key={r.label} className="flex items-center justify-between">
                <span className="text-[9px] text-slate-500">{r.label}</span>
                <span className={`text-[10px] font-semibold tabular-nums ${r.color || 'text-slate-700'}`}>{r.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* System Verdict */}
      <div className="bg-white rounded-lg border border-slate-200 p-3">
        <div className="text-[8px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">Verdict</div>
        <div className="flex items-center gap-2">
          <span className={`text-[12px] font-extrabold px-2.5 py-0.5 rounded ${
            effectiveVerdict === 'GO' ? 'bg-emerald-600 text-white' :
            effectiveVerdict === 'WATCH' ? 'bg-amber-500 text-white' : 'bg-red-600 text-white'
          }`} data-testid="effective-verdict">{effectiveVerdict}</span>
          <span className="text-[9px] text-slate-500">{effectiveReason}</span>
        </div>
      </div>
    </div>
  );
}
